import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import * as auth from "./services/jbwAuthServices";
import SessionExpiredDialog from "./components/SessionExpiredDialog";
import GlobalLoader from "./components/GlobalLoader";
import useIdleTimer from "./hooks/useIdleTimer";
const APP_ENV = process.env.REACT_APP_MYVARNAME;

const AuthContext = createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [requiredNewPwd, setRequiredNewPwd] = useState(false);
  const [newPasswordAttr, setNewPasswordAttr] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dealer, setDealer] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(true);
  const [group, setGroup] = useState("");
  const [loginError, setLoginError] = useState("");
  const [mailMessage, setMailMessage] = useState(null);
  const [ctxStEquipments, setCtxStEquipments] = useState([]);
  const [ctxEquipmentTypes, setCtxEquipmentTypes] = useState([]);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState("expired");
  const [loginUser, setLoginUser] = useState(null);

  const currentSession = useCallback(async () => {
    try {
      const session = await auth.getSession();
      setSession(session);

      localStorage.setItem("token", session.getIdToken().getJwtToken());

      const groups = session.idToken.payload["cognito:groups"] || [];
      console.log("Groups retrieved:", groups);
      if (groups && groups.length > 0) {
        setGroup(groups[0]);
      } else {
        setGroup("");
        console.log("No Cognito groups found for the user");
      }
    } catch (err) {
      console.error("Session error:", err);
      setSession(null);
      setGroup("");
      localStorage.removeItem("token");
    }
  }, []);

  const getCurrentUser = useCallback(async () => {
    try {
      const user = await auth.getCurrentUser();
      console.log("Current user fetched:", user);
      setUser(user);
      await currentSession();
      setLoginError("");
    } catch (err) {
      setUser(null);
      setSession(null);
      setGroup("");
    }
  }, [currentSession]);

  const handleSessionExpired = useCallback((reason = "expired") => {
    setSessionExpiredReason(reason);
    setShowSessionDialog(true);
  }, []);

  const handleIdleTimeout = useCallback(() => {
    if (user && !showSessionDialog) {
      handleSessionExpired("idle");
    }
  }, [user, showSessionDialog, handleSessionExpired]);

  const { pause: pauseIdleTimer, resume: resumeIdleTimer } = useIdleTimer(
    handleIdleTimeout,
    30 * 60 * 1000
  );

  useEffect(() => {
    if (showSessionDialog) {
      pauseIdleTimer();
    } else if (user) {
      resumeIdleTimer();
    }
  }, [showSessionDialog, user, pauseIdleTimer, resumeIdleTimer]);

  const handleReauthenticate = useCallback(
    async (password) => {
      if (!user?.username && !user?.email) {
        throw new Error("No username found");
      }

      const username = user.username || user.email;

      try {
        await auth.signIn(username, password);
        await getCurrentUser();
        setShowSessionDialog(false);
        resumeIdleTimer();
      } catch (error) {
        throw error;
      }
    },
    [user, getCurrentUser, resumeIdleTimer]
  );

  const handleCancelReauth = useCallback(async () => {
    setShowSessionDialog(false);
    pauseIdleTimer();
    await signOut();
  }, [pauseIdleTimer]);

  const checkAndRefreshSession = useCallback(async () => {
    try {
      const isValid = await auth.isSessionValid();

      if (!isValid) {
        handleSessionExpired("expired");
        return;
      }

      const expirationTime = await auth.getTokenExpirationTime();
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;
      const fiveMinutes = 5 * 60 * 1000;

      if (timeUntilExpiry < fiveMinutes) {
        try {
          const newSession = await auth.refreshSession();
          const newToken = newSession.getIdToken().getJwtToken();
          localStorage.setItem("token", newToken);
          setSession(newSession);
        } catch (refreshError) {
          handleSessionExpired("expired");
        }
      }
    } catch (err) {
      console.error("Session check error:", err);
      handleSessionExpired("expired");
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    getCurrentUser().finally(() => setIsLoading(false));
  }, [getCurrentUser]);

  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      checkAndRefreshSession();
    }, 60000);

    return () => clearInterval(interval);
  }, [user, checkAndRefreshSession]);

  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      handleSessionExpired("expired");
    };

    window.addEventListener("session-expired", handleSessionExpiredEvent);

    return () => {
      window.removeEventListener("session-expired", handleSessionExpiredEvent);
    };
  }, [handleSessionExpired]);

  const signIn = useCallback(
    async (username, password) => {
      localStorage.removeItem("dealer");
      localStorage.removeItem("token");
      setLoginError("");

      try {
        auth.signIn(username, password).then(
          (res) => {
            console.log("New Password Required:", res.newpasswordreq);
            if (res.newpasswordreq) {
              setRequiredNewPwd(true);
              setNewPasswordAttr(res);
            } else {
              console.log("SignIn successful:", res);
              getCurrentUser();
            }
          },
          (err) => {
            console.error("SignIn failed:", err);
            setLoginError(err.message || "Authentication failed. Please try again.");
          }
        );
      } catch (error) {
        console.error("SignIn error:", error);
        localStorage.removeItem("dealer");
        localStorage.removeItem("token");

        if (
          error.name === "UserNotFoundException" ||
          error.name === "NotAuthorizedException"
        ) {
          setLoginError(error.message);
        } else {
          setLoginError("Authentication failed. Please try again.");
        }
        throw error;
      }
    },
    [getCurrentUser]
  );

  const resetNewPasswordAttr = () => {
    setNewPasswordAttr(null);
    setRequiredNewPwd(false);
  };

  const signOut = useCallback(async () => {
    pauseIdleTimer();
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Error during sign out:", error);
    } finally {
      resetNewPasswordAttr();
      setUser(null);
      setSession(null);
      setDealer(null);
      setGroup("");
      setIsLoading(true);
      setOnboarding(null);
      setLoginError("");
      setIsOnboarded(true);
      setMailMessage(null);
      setLoginUser(null);
      localStorage.removeItem("dealer");
      localStorage.removeItem("dealerSycState");
      localStorage.removeItem("token");
      window.location.href = "/";
    }
  }, [pauseIdleTimer]);

  const authValue = useMemo(
    () => ({
      user,
      requiredNewPwd,
      newPasswordAttr,
      session,
      isLoading,
      signIn,
      signOut,
      dealer,
      group,
      onboarding,
      isOnboarded,
      setDealer,
      setOnboarding,
      loginError,
      setIsOnboarded,
      mailMessage,
      setMailMessage,
      ctxStEquipments,
      setCtxStEquipments,
      ctxEquipmentTypes,
      setCtxEquipmentTypes,
      resetNewPasswordAttr,
      getCurrentUser,
      loginUser,
      setLoginUser,
      APP_ENV,
    }),
    [
      user,
      requiredNewPwd,
      newPasswordAttr,
      session,
      isLoading,
      signIn,
      signOut,
      dealer,
      group,
      onboarding,
      isOnboarded,
      loginError,
      mailMessage,
      ctxEquipmentTypes,
      ctxStEquipments,
      loginUser,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <GlobalLoader />
      {children}
      <SessionExpiredDialog
        open={showSessionDialog}
        onReauthenticate={handleReauthenticate}
        onCancel={handleCancelReauth}
        username={user?.name || user?.email}
        reason={sessionExpiredReason}
      />
    </AuthContext.Provider>
  );
}

export { AuthProvider, AuthContext };
